"use client";

import Image from "next/image";
import { useState, useSyncExternalStore } from "react";
import { EllipsisVertical, Share, SquarePlus, X } from "lucide-react";
import BottomSheet from "@/components/ui/ButtomSheet";
import {
  PWA_PLATFORM,
  detect_pwa_platform,
  is_running_standalone,
} from "@/helpers/pwaInstall";

// تغییر حالت موبایل در devtools یا چرخش صفحه فقط یک resize می‌دهد و UserAgent بدون ریلود عوض می‌شود
const subscribe = (on_change) => {
  window.addEventListener("resize", on_change);
  window.addEventListener("orientationchange", on_change);
  return () => {
    window.removeEventListener("resize", on_change);
    window.removeEventListener("orientationchange", on_change);
  };
};

// سرور و اولین رندر کلاینت null می‌دهند تا hydration به هم نریزد
const get_platform_snapshot = () =>
  is_running_standalone() ? null : detect_pwa_platform();

// دکمه‌ی کوچکی که در متن مرحله‌ها اسم گزینه‌ی مرورگر را نشان می‌دهد
function Chip({ icon, children }) {
  return (
    <span
      dir="ltr"
      className="inline-flex h-7 items-center justify-center gap-1 align-middle rounded-sm bg-jmt-neutral-100 px-2 text-sm leading-none font-bold text-jmt-neutral-900 mx-1 [&>svg]:shrink-0"
    >
      {icon}
      {/* فونت شبنم گلیف‌های لاتین را بالای خط می‌نشاند؛ با کمی padding به مرکز می‌آید */}
      {children && <span className="pt-1">{children}</span>}
    </span>
  );
}

const STEPS = {
  [PWA_PLATFORM.IOS]: [
    <>
      در نوار پایین <span dir="ltr">Safari</span> روی دکمه‌ی
      <Chip icon={<Share size={20} />}>Share</Chip>
      بزنید.
    </>,
    <>
      در منوی باز شده، گزینه‌ی
      <Chip icon={<SquarePlus size={20} />}>Add to Home Screen</Chip>
      را انتخاب کنید.
    </>,
    <>
      در بالای صفحه روی
      <Chip>Add</Chip>
      بزنید.
    </>,
  ],
  [PWA_PLATFORM.ANDROID]: [
    <>
      در مرورگر <span dir="ltr">Chrome</span> روی
      <Chip icon={<EllipsisVertical size={20} />} />
      در بالای صفحه بزنید.
    </>,
    <>
      از منوی باز شده، گزینه‌ی
      <Chip icon={<SquarePlus size={20} />}>Add to Home screen</Chip>
      یا
      <Chip>Install app</Chip>
      را انتخاب کنید.
    </>,
    <>
      در پنجره‌ی باز شده روی
      <Chip>Install</Chip>
      بزنید.
    </>,
  ],
};

const to_fa_digit = (n) => String(n).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

/**
 * آیتم «وب اپلیکیشن ژومنت» در منوی فوتر موبایل. فقط روی اندروید/iOS دیده می‌شود
 * و بسته به سیستم‌عامل، مودال راهنمای مخصوص همان را باز می‌کند.
 */
export default function PwaInstallItem({ title, className = "" }) {
  const [open, set_open] = useState(false);

  const platform = useSyncExternalStore(
    subscribe,
    get_platform_snapshot,
    () => null,
  );

  if (!platform) return null;

  return (
    <div className={className}>
      {/* کل باکس قابل کلیک است و مودال را باز می‌کند */}
      <button
        type="button"
        onClick={() => set_open(true)}
        className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-jmt-neutral-700 bg-jmt-neutral-800 p-3"
      >
        <Image
          src="/images/logo/ic-jument-filled.svg"
          unoptimized
          alt=""
          width={48}
          height={48}
          className="shrink-0"
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-right">
          <span className="text-sm font-bold leading-[22px] text-jmt-text-dark-mode">
            {title}
          </span>
          <span className="text-xs leading-[18px] text-jmt-neutral-400">
            بدون دانلود از استور، روی موبایلت نصبش کن.
          </span>
        </span>
        <span className="flex h-10 w-[66px] shrink-0 items-center justify-center rounded-sm border border-jmt-neutral-800 bg-jmt-white px-4 py-2 text-xs font-bold leading-[18px] text-jmt-neutral-800">
          نصب
        </span>
      </button>

      <BottomSheet
        open={open}
        onClose={() => set_open(false)}
        partialHeight="min(440px, 90dvh)"
      >
        <div dir="rtl" className="flex h-full flex-col px-4 pb-4 pt-1">
          <div className="flex shrink-0 items-center justify-between border-b border-jmt-neutral-200 py-2">
            <h3 className="text-sm font-bold text-jmt-neutral-900">
              افزودن ژومنت به صفحه اصلی
            </h3>
            <button
              type="button"
              aria-label="بستن"
              onClick={() => set_open(false)}
              className="cursor-pointer text-jmt-neutral-900"
            >
              <X size={24} />
            </button>
          </div>

          {/* فقط بدنه اسکرول می‌خورد؛ هدر و دکمه ثابت می‌مانند */}
          <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col items-center gap-3 pt-5">
            {/* لوگو در فیگما به رنگ اصلی برند است؛ تصویر مشکی به‌عنوان ماسک رنگ می‌گیرد */}
            <span
              role="img"
              aria-label="لوگو ژومنت"
              className="block h-7 w-24 bg-jmt-primary-900"
              style={{
                maskImage: "url(/images/logo/logoBlack.png)",
                WebkitMaskImage: "url(/images/logo/logoBlack.png)",
                maskSize: "contain",
                WebkitMaskSize: "contain",
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",
                maskPosition: "center",
                WebkitMaskPosition: "center",
              }}
            />
            <p className="text-center text-base font-bold leading-6 text-jmt-neutral-900">
              وب اپلیکیشن ژومنت رو به صفحه اصلی موبایل خود اضافه کنید.
            </p>
          </div>

          <ol className="flex flex-col gap-2 mt-8">
            {STEPS[platform].map((step, index) => (
              <li
                key={index}
                className="flex gap-1 text-sm leading-8 text-jmt-neutral-900"
              >
                <span className="font-bold">{to_fa_digit(index + 1)}.</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          </div>

          <button
            type="button"
            onClick={() => set_open(false)}
            className="mt-5 h-12 w-full shrink-0 cursor-pointer rounded-sm border border-jmt-neutral-900 text-sm font-bold text-jmt-neutral-900"
          >
            متوجه شدم
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}
